import type { Icon } from "@phosphor-icons/react";
import { ChartBarIcon } from "@phosphor-icons/react/dist/csr/ChartBar";
import { ChartLineIcon } from "@phosphor-icons/react/dist/csr/ChartLine";
import { FilesIcon } from "@phosphor-icons/react/dist/csr/Files";
import { GearSixIcon } from "@phosphor-icons/react/dist/csr/GearSix";
import { ListChecksIcon } from "@phosphor-icons/react/dist/csr/ListChecks";
import { PlusIcon } from "@phosphor-icons/react/dist/csr/Plus";
import { RobotIcon } from "@phosphor-icons/react/dist/csr/Robot";
import { SquaresFourIcon } from "@phosphor-icons/react/dist/csr/SquaresFour";
import { TableIcon } from "@phosphor-icons/react/dist/csr/Table";
import { matchPath } from "react-router-dom";
import type { SidebarSection } from "@/app-shell";
import type { ReportOutput } from "@/profitability/types";
import {
  PROFITABILITY_AGENTS_PATH,
  PROFITABILITY_NEW_TEMPLATE_PATH,
  PROFITABILITY_PATH,
  profitabilityTemplatePanePath,
  profitabilityTemplatePath,
  profitabilityTemplateSettingsPath,
} from "./routes";
import {
  currentTab,
  paneOf,
  tabOf,
  templatePageTabs,
  type TemplateTab,
} from "./template-page/template-tabs";

const AGENTS = "Agents";
const PLACEHOLDERS = 2;

const TAB_ICON: Record<string, Icon> = {
  dashboard: ChartBarIcon,
  review: ListChecksIcon,
  insights: ChartLineIcon,
  workbook: TableIcon,
  sheet: TableIcon,
  documents: FilesIcon,
};

export const tabIcon = (value: string) => TAB_ICON[value] ?? SquaresFourIcon;

export const loadingRows = (
  key = "column",
  count = PLACEHOLDERS + 1,
  group?: string,
): SidebarSection[] =>
  Array.from({ length: count }, (_, index) => ({
    label: "",
    to: `#${key}-${index}`,
    isActive: false,
    group,
    pending: true,
  }));

export const placeholders = (group: string) =>
  loadingRows(group, PLACEHOLDERS, group);

const PAGE_SKELETONS = 4;

export const loadingPages = (reportId: string) =>
  loadingRows(reportId, PAGE_SKELETONS);

/** `?period=` and `?compare=` from the page's URL, for every tab's link. */
export const periodSearch = (search: string) => {
  const current = new URLSearchParams(search);
  const kept = new URLSearchParams();
  for (const key of ["period", "compare"]) {
    const value = current.get(key);
    if (value) kept.set(key, value);
  }
  const text = kept.toString();
  return text ? `?${text}` : "";
};

/** The open agent's pages: the tabs of the period on screen. */
export const agentTabs = ({
  reportId,
  tabs,
  current,
  search,
}: {
  reportId: string;
  tabs: TemplateTab[];
  current: string | null;
  search: string;
}): SidebarSection[] =>
  tabs.map((tab) => {
    const pane = paneOf(tab.value);
    const path = pane
      ? profitabilityTemplatePanePath(reportId, pane)
      : `${profitabilityTemplatePath(reportId)}/${tab.value}`;
    return {
      label: tab.label,
      to: `${path}${periodSearch(search)}`,
      isActive: current === tab.value,
      icon: tabIcon(tab.value),
      ...(tab.count
        ? {
            count: tab.count,
            countLabel: tab.value === "review" ? "to review" : undefined,
          }
        : {}),
    };
  });

/**
 * Every agent the user can open. The one on screen is expanded unless folded;
 * any other expands on its chevron, which loads its latest period's pages, so
 * a page of another agent opens straight from the list. Only the agent on
 * screen marks a page as current. Settings is the button at each row's end.
 */
export const agentList = ({
  agents,
  currentId,
  settings,
  pages,
  isExpanded,
  onExpandedChange,
  onIntent,
}: {
  agents: { id: string; name: string }[] | undefined;
  currentId: string | null;
  settings: boolean;
  pages: Record<string, SidebarSection[] | undefined>;
  isExpanded: (id: string) => boolean;
  onExpandedChange?: (id: string, expanded: boolean) => void;
  onIntent?: (id: string) => void;
}): SidebarSection[] =>
  agents
    ? agents.map((agent) => {
        const isCurrent = agent.id === currentId;
        const own = pages[agent.id];
        return {
          label: agent.name,
          to: profitabilityTemplatePath(agent.id),
          // An agent with no pages yet (a draft) is itself the page.
          isActive:
            isCurrent && !settings && !own?.some((page) => page.isActive),
          icon: RobotIcon,
          group: AGENTS,
          expanded: isExpanded(agent.id),
          children: own,
          onExpandedChange:
            onExpandedChange &&
            ((expanded: boolean) => onExpandedChange(agent.id, expanded)),
          onIntent:
            onIntent && !isCurrent && !own
              ? () => onIntent(agent.id)
              : undefined,
          action: {
            label: "Settings",
            to: profitabilityTemplateSettingsPath(agent.id),
            icon: GearSixIcon,
            isActive: isCurrent && settings,
          },
        };
      })
    : placeholders(AGENTS);

/** New agent, beside All agents above the list, for whoever writes agents. */
export const newAgent = (pathname: string): SidebarSection => ({
  label: "New agent",
  to: PROFITABILITY_NEW_TEMPLATE_PATH,
  isActive: pathname.startsWith(PROFITABILITY_NEW_TEMPLATE_PATH),
  icon: PlusIcon,
});

/** The Job Costing home: the agents, or in ServiceTitan mode its runs. */
export const jobCostingHome = (
  pathname: string,
  label: string,
): SidebarSection => ({
  label,
  to: PROFITABILITY_PATH,
  isActive: pathname.replace(/\/+$/, "") === PROFITABILITY_PATH,
  icon: SquaresFourIcon,
});

/** The agent route, as one shape: a pane, a section, or the agent itself. */
export const agentRoute = (
  pathname: string,
): { reportId: string; section?: string; pane?: string } | null => {
  const pane = matchPath(
    `${PROFITABILITY_AGENTS_PATH}/:reportId/pane/:pane`,
    pathname,
  );
  if (pane?.params.reportId)
    return { reportId: pane.params.reportId, pane: pane.params.pane };
  const page = matchPath(
    `${PROFITABILITY_AGENTS_PATH}/:reportId/:section?/*`,
    pathname,
  );
  if (!page?.params.reportId || page.params.reportId === "new") return null;
  return { reportId: page.params.reportId, section: page.params.section };
};

/**
 * Agents for the column. A failed refresh keeps the ones already loaded;
 * a list that never loaded shows the open agent, or none.
 */
export const columnAgents = ({
  loaded,
  failed,
  report,
  listed,
}: {
  loaded: { id: string; name: string }[] | undefined;
  failed: boolean;
  report?: { id: string; name: string };
  listed: boolean;
}): { id: string; name: string }[] | undefined => {
  if (loaded) {
    if (report && !listed) return [...loaded, report];
    return loaded;
  }
  if (!failed) return undefined;
  if (!report) return [];
  return [report];
};

/**
 * The open agent's pages. Skeleton rows hold them until the period is in.
 * Dashboard, Review, Insights, Workbook and Documents are known from that
 * period, so they show while the rows (and the script's panes) are still
 * loading. A stand-in from another period keeps the review count and drops
 * the others.
 */
export const openAgentPages = ({
  reportId,
  section,
  pane,
  search,
  overview,
  output,
  activeCount,
  findingsPending,
}: {
  reportId: string;
  section?: string;
  pane?: string;
  search: string;
  overview: {
    data:
      | {
          run: { interactive_available: boolean } | null;
          review?: { work?: string | null } | null;
        }
      | undefined;
    pending: boolean;
    stale: boolean;
  };
  output: {
    data: ReportOutput | undefined;
    pending: boolean;
    stale: boolean;
  };
  activeCount: number;
  findingsPending: boolean;
}): SidebarSection[] => {
  const period = overview.data?.run ?? null;
  if (overview.pending || !overview.data) return loadingPages(reportId);
  const needsOutput = !!period?.interactive_available;
  const rowsReady = !needsOutput || (!output.pending && !!output.data);
  const fresh = templatePageTabs({
    output: rowsReady ? output.data : undefined,
    work: overview.data?.review?.work ?? null,
    documents: !!period,
    activeCount: findingsPending ? 0 : activeCount,
  });
  const stale = overview.stale || (needsOutput && output.stale);
  const tabs = stale
    ? fresh.tabs.map((tab) =>
        tab.value === "review" ? tab : { ...tab, count: undefined },
      )
    : fresh.tabs;
  const onTab = !section || tabs.some((tab) => tab.value === tabOf(section));
  return agentTabs({
    reportId,
    tabs,
    current: onTab
      ? currentTab({
          section,
          pane,
          tabs,
          review: fresh.review,
          waiting: false,
        })
      : null,
    search,
  });
};
